import React from "react";
import {
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Badge,
} from "reactstrap";

/**
 * Search + filter + actions toolbar used directly above any table
 * that's not wrapped in DataTableShell. Visual language matches
 * DataTableShell's header so the dashboard looks consistent whether
 * the consumer uses the full shell or just the toolbar.
 *
 * Pure presentation — controlled by the caller. Does not own any
 * search state, does not debounce, does not fetch.
 *
 * Use this when the page already has its own Card wrapping the table
 * (e.g. AllDms with stat tiles + table in the same card layout) and
 * a full DataTableShell migration would touch too much logic.
 *
 * Props
 *  - title:           heading text shown on the left (optional)
 *  - subtitle:        small line under the title (optional)
 *  - rowCount:        number — displays a "{count} rows" badge
 *  - countLabel:      override the "rows" label
 *  - searchValue:     controlled search value
 *  - onSearchChange:  (string) => void
 *  - searchPlaceholder
 *  - onRefresh:       optional () => void
 *  - refreshing:      bool — disables refresh button + animates icon
 *  - actions:         React node — extra buttons (Create, Export, etc)
 *  - filters:         React node — filter chips/dropdowns row, rendered
 *                     BELOW the search row on its own line
 *  - className:       extra wrapper className
 *  - dense:           bool — tighter vertical padding
 */
const StandardTableToolbar = ({
  title,
  subtitle,
  rowCount,
  countLabel = "rows",
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  onRefresh,
  refreshing = false,
  actions,
  filters,
  className = "",
  dense = false,
}) => {
  const showHeader = title || typeof rowCount === "number";

  return (
    <div className={`dk-table-toolbar ${className}`} style={{ marginBottom: dense ? 8 : 12 }}>
      {showHeader ? (
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
          <div style={{ minWidth: 0 }}>
            {title ? (
              <h6 className="mb-0 fw-semibold" style={{ color: "#212529" }}>
                {title}
              </h6>
            ) : null}
            {subtitle ? (
              <p
                className="text-muted small mb-0"
                style={{ fontSize: 12 }}
              >
                {subtitle}
              </p>
            ) : null}
          </div>

          {typeof rowCount === "number" ? (
            <Badge
              color="light"
              className="text-muted border fw-medium"
              style={{ padding: "5px 9px", fontSize: 11 }}
            >
              <i className="ri-database-2-line align-bottom me-1" />
              {rowCount.toLocaleString()} {countLabel}
            </Badge>
          ) : null}
        </div>
      ) : null}

      <div className="d-flex flex-wrap align-items-center gap-2">
        {typeof onSearchChange === "function" ? (
          <InputGroup style={{ maxWidth: 360, flex: "1 1 240px" }}>
            <InputGroupText className="bg-light border-end-0">
              <i className="ri-search-line" />
            </InputGroupText>
            <Input
              type="text"
              className="border-start-0"
              placeholder={searchPlaceholder}
              value={searchValue || ""}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </InputGroup>
        ) : null}

        <div className="d-flex flex-wrap gap-2 ms-auto">
          {actions}

          {typeof onRefresh === "function" ? (
            <Button
              color="light"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
              title="Refresh"
            >
              <i
                className={`ri-refresh-line align-bottom me-1 ${
                  refreshing ? "dk-tt-spin" : ""
                }`}
              />
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          ) : null}
        </div>
      </div>

      {filters ? (
        <div className="mt-2 pt-2 border-top">
          {filters}
        </div>
      ) : null}

      <style>{`
        .dk-tt-spin {
          animation: dk-tt-spin-anim 0.9s linear infinite;
          display: inline-block;
        }
        @keyframes dk-tt-spin-anim {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StandardTableToolbar;
